import { Check, X, Minus } from "lucide-react";

const rows = [
  {
    feature: "Failure State",
    oem: { v: "Closed", bad: true, sub: "Engine overheats" },
    mps: { v: "Open", good: true, sub: "Full coolant flow" },
  },
  {
    feature: "Engineering Origin",
    oem: { v: "Legacy OE", neutral: true, sub: "Original platform design" },
    mps: { v: "Re-engineered", good: true, sub: "Ground-up improvement" },
  },
  {
    feature: "Return Spring",
    oem: { v: "Standard", bad: true, sub: "Under-sized" },
    mps: { v: "Reinforced", good: true, sub: "Thicker wire, higher pre-load" },
  },
  {
    feature: "Inlet Filter",
    oem: { v: "None", bad: true, sub: "Debris ingress" },
    mps: { v: "Integrated", good: true, sub: "Protects secondary pump" },
  },
  {
    feature: "Secondary Pump Type",
    oem: { v: "Piston <10mm", bad: true, sub: "Debris-sensitive" },
    mps: { v: "Flexible impeller", good: true, sub: "Filter-protected" },
  },
  {
    feature: "Flow Collector",
    oem: { v: "—", neutral: true, sub: "N/A" },
    mps: { v: "Eliminated", good: true, sub: "Back-face vanes replace it" },
  },
  {
    feature: "Warranty Exposure",
    oem: { v: "High", bad: true, sub: "Known failure pattern" },
    mps: { v: "Minimal", good: true, sub: "Engineered out" },
  },
];

export default function Compare() {
  return (
    <section
      id="compare"
      data-testid="compare-section"
      className="relative py-28 lg:py-36 bg-white overflow-hidden"
    >
      <div className="absolute inset-0 tech-grid opacity-15 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        <div className="max-w-3xl mb-14 reveal">
          <span className="spec-label">Comparison Matrix</span>
          <h2 className="font-chivo font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A2232] leading-[0.95] mt-5">
            MPS MWP1110 vs. OEM Coolant Pump —<br />
            <span className="text-[#5B9BD5]">side by side.</span>
          </h2>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block reveal">
          <div className="border border-[#C8D3DF] rounded-sm overflow-hidden">
            {/* header */}
            <div className="grid grid-cols-9 bg-white border-b border-[#C8D3DF]">
              <div className="col-span-3 p-5 font-mono text-[10px] tracking-[0.3em] text-[#8896A6] uppercase">
                Specification
              </div>
              <ColHeader label="OEM" tag="ORIGINAL" bad />
              <ColHeader label="MPS MWP1110" tag="ENGINEERED" mps />
            </div>

            {rows.map((r, i) => (
              <div
                key={r.feature}
                data-testid={`compare-row-${i}`}
                className={`grid grid-cols-9 ${
                  i % 2 === 0 ? "bg-[#F4F6FA]" : "bg-white"
                } border-b border-[#E8EEF6] last:border-0 hover:bg-[#EAF0F8]/40 transition-colors`}
              >
                <div className="col-span-3 p-5 flex items-center font-chivo font-bold text-[#1A2232] text-sm">
                  {r.feature}
                </div>
                <Cell data={r.oem} />
                <Cell data={r.mps} mps />
              </div>
            ))}
          </div>
        </div>

        {/* Mobile stacked */}
        <div className="md:hidden space-y-6">
          {rows.map((r, i) => (
            <div key={r.feature} className="border border-[#C8D3DF] rounded-sm p-5 bg-white reveal">
              <div className="font-mono text-[10px] tracking-[0.25em] text-[#8896A6] uppercase mb-3">
                {r.feature}
              </div>
              <div className="space-y-2">
                <MobileRow label="OEM" data={r.oem} />
                <MobileRow label="MPS MWP1110" data={r.mps} mps />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ColHeader({ label, tag, bad, mps }) {
  return (
    <div
      className={`col-span-3 p-5 border-l border-[#C8D3DF] flex flex-col ${
        mps ? "bg-[#5B9BD5]/5" : ""
      }`}
    >
      <span
        className={`font-mono text-[9px] tracking-[0.3em] uppercase ${
          bad ? "text-[#FF5722]/80" : "text-[#5B9BD5]"
        }`}
      >
        {tag}
      </span>
      <span className="font-chivo font-black text-[#1A2232] text-base mt-1">{label}</span>
    </div>
  );
}

function Cell({ data, mps }) {
  return (
    <div className={`col-span-3 p-5 border-l border-[#E8EEF6] ${mps ? "bg-[#5B9BD5]/[0.03]" : ""}`}>
      <div className="flex items-center gap-2">
        {data.good ? (
          <Check size={14} className="text-[#5B9BD5] shrink-0" />
        ) : data.bad ? (
          <X size={14} className="text-[#FF5722] shrink-0" />
        ) : (
          <Minus size={14} className="text-[#8896A6] shrink-0" />
        )}
        <span
          className={`font-chivo font-bold text-sm ${
            data.good ? "text-[#5B9BD5]" : data.bad ? "text-[#1A2232]" : "text-[#536073]"
          }`}
        >
          {data.v}
        </span>
      </div>
      {data.sub && (
        <div className="font-plex text-xs text-[#8896A6] mt-1 ml-6">{data.sub}</div>
      )}
    </div>
  );
}

function MobileRow({ label, data, mps }) {
  return (
    <div
      className={`flex items-center justify-between gap-3 py-2 px-3 rounded-sm ${
        mps ? "bg-[#5B9BD5]/8 border border-[#5B9BD5]/30" : "bg-[#F4F6FA] border border-[#E8EEF6]"
      }`}
    >
      <span className="font-chivo font-bold text-xs text-[#1A2232]">{label}</span>
      <div className="flex items-center gap-2">
        {data.good ? (
          <Check size={12} className="text-[#5B9BD5]" />
        ) : data.bad ? (
          <X size={12} className="text-[#FF5722]" />
        ) : (
          <Minus size={12} className="text-[#8896A6]" />
        )}
        <span
          className={`font-chivo font-bold text-xs ${
            data.good ? "text-[#5B9BD5]" : data.bad ? "text-[#1A2232]" : "text-[#536073]"
          }`}
        >
          {data.v}
        </span>
      </div>
    </div>
  );
}
